/**
 * Animation toggle state, shared globally via Context so that every component
 * calling useAnimation() reads/writes the same value. (Without Context, each
 * useState() call would create an independent piece of state — toggling in
 * the SettingsDropdown wouldn't be visible to the HomePage hero, etc.)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AnimationState } from "../types";

const STORAGE_KEY = "si.animation";

interface AnimationContextValue {
  animationState: AnimationState;
  toggleAnimation: () => void;
}

const AnimationContext = createContext<AnimationContextValue | null>(null);

function detectInitial(): AnimationState {
  if (typeof window === "undefined") return "on";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "on" || stored === "off") return stored;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "off" : "on";
}

export function AnimationProvider({ children }: { children: ReactNode }) {
  const [animationState, setAnimationState] = useState<AnimationState>(detectInitial);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, animationState);
  }, [animationState]);

  const toggleAnimation = useCallback(() => {
    setAnimationState((s) => (s === "on" ? "off" : "on"));
  }, []);

  const value = useMemo(
    () => ({ animationState, toggleAnimation }),
    [animationState, toggleAnimation],
  );

  return <AnimationContext.Provider value={value}>{children}</AnimationContext.Provider>;
}

export function useAnimation(): AnimationContextValue {
  const ctx = useContext(AnimationContext);
  if (!ctx) throw new Error("useAnimation must be used inside <AnimationProvider>");
  return ctx;
}
